import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Plus, Search, Filter, Clock, AlertCircle, 
  MessageSquare, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'IT'
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await fetch(`${API}/tickets`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const res = await fetch(`${API}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTicket)
      });

      if (res.ok) {
        const created = await res.json();
        setTickets([created, ...tickets]);
        setDialogOpen(false);
        setNewTicket({ subject: '', description: '', priority: 'medium', category: 'IT' });
        toast.success('Ticket created successfully!');
      } else {
        toast.error('Failed to create ticket');
      }
    } catch (error) {
      console.error('Create ticket error:', error);
      toast.error('Something went wrong');
    } finally {
      setCreating(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.subject.toLowerCase().includes(search.toLowerCase()) ||
                          ticket.description.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
      in_progress: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
      resolved: 'text-green-400 bg-green-400/10 border-green-400/20',
      closed: 'text-gray-400 bg-gray-400/10 border-gray-400/20'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-400',
      medium: 'text-yellow-400',
      high: 'text-orange-400',
      critical: 'text-red-400'
    };
    return colors[priority] || 'text-gray-400';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="tickets-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
      >
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Support Tickets</h1>
          <p className="text-gray-400">Manage and track your support requests</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold shadow-[0_4px_12px_rgba(59,130,246,0.2)] border-none"
              data-testid="create-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0D1224] border-blue-500/15 text-white max-w-lg shadow-2xl shadow-black/80">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">Create Support Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={createTicket} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2 font-medium">Subject *</label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                  required
                  placeholder="Brief description of your issue"
                  className="bg-[#080B14]/40 border-blue-500/15 text-white placeholder:text-gray-500 focus:border-blue-500/50"
                  data-testid="ticket-subject-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2 font-medium">Category</label>
                  <Select 
                    value={newTicket.category}
                    onValueChange={(value) => setNewTicket({...newTicket, category: value})}
                  >
                    <SelectTrigger className="bg-[#080B14]/40 border-blue-500/15 text-white focus:border-blue-500/50" data-testid="ticket-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0D1224] border-blue-500/15 text-white">
                      <SelectItem value="IT">IT Support</SelectItem>
                      <SelectItem value="Networking">Networking</SelectItem>
                      <SelectItem value="CCTV">CCTV</SelectItem>
                      <SelectItem value="Software">Software</SelectItem>
                      <SelectItem value="Billing">Billing</SelectItem>
                      <SelectItem value="General">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2 font-medium">Priority</label>
                  <Select 
                    value={newTicket.priority}
                    onValueChange={(value) => setNewTicket({...newTicket, priority: value})}
                  >
                    <SelectTrigger className="bg-[#080B14]/40 border-blue-500/15 text-white focus:border-blue-500/50" data-testid="ticket-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0D1224] border-blue-500/15 text-white">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 font-medium">Description *</label>
                <Textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                  required
                  rows={4}
                  placeholder="Provide detailed information about your issue..."
                  className="bg-[#080B14]/40 border-blue-500/15 text-white resize-none placeholder:text-gray-500 focus:border-blue-500/50"
                  data-testid="ticket-description-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-blue-500/20 text-white bg-transparent hover:bg-blue-500/5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold border-none shadow-[0_2px_10px_rgba(59,130,246,0.2)]"
                  data-testid="submit-ticket-btn"
                >
                  {creating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Create Ticket'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row gap-4 mb-6"
      >
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tickets..."
            className="pl-10 bg-[#080B14]/40 border-blue-500/15 text-white placeholder:text-gray-500 focus:border-blue-500/50"
            data-testid="search-tickets"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-[#080B14]/40 border-blue-500/15 text-white focus:border-blue-500/50" data-testid="filter-status">
            <Filter className="w-4 h-4 mr-2 text-gray-400" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#0D1224] border-blue-500/15 text-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No tickets found</h3>
          <p className="text-gray-400 mb-6">
            {tickets.length === 0 
              ? "You haven't created any support tickets yet." 
              : "No tickets match your search criteria."
            }
          </p>
          {tickets.length === 0 && (
            <Button 
              onClick={() => setDialogOpen(true)}
              className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold border-none hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] shadow-[0_4px_12px_rgba(59,130,246,0.2)]"
            >
              Create Your First Ticket
            </Button>
          )}
        </motion.div>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket, idx) => (
            <motion.div
              key={ticket.ticket_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Link
                to={`/tickets/${ticket.ticket_id}`}
                className="block p-6 rounded-xl transition-all hover:bg-blue-500/5 hover:border-blue-500/30 group"
                style={{ background: '#0D1224', border: '1px solid rgba(99, 130, 255, 0.12)' }}
                data-testid={`ticket-${ticket.ticket_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-medium truncate group-hover:text-blue-400 transition-colors">{ticket.subject}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-1 mb-3">{ticket.description}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="px-2 py-1 rounded-full bg-blue-500/10 text-blue-400/90 border border-blue-500/20">{ticket.category}</span>
                      <span className={getPriorityColor(ticket.priority)}>
                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)} Priority
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-blue-400" />
                        {formatDate(ticket.created_at)}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-blue-400 transition-colors" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
